import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { settingsApi } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import PublicLayout from "@/components/PublicLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAnalysis from "@/pages/AdminAnalysis";
import AdminSettings from "@/pages/AdminSettings";
import AdminPdfSettings from "@/pages/AdminPdfSettings";
import AdminUsers from "@/pages/AdminUsers";
import ProfilePage from "@/pages/ProfilePage";
import PdfCompanyPage from "@/pages/PdfCompanyPage";
import PublicWizard from "@/pages/PublicWizard";
import Datenschutz from "@/pages/Datenschutz";
import InfoPage from "@/pages/InfoPage";
import FaqPage from "@/pages/FaqPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import PasswordResetConfirmPage from "@/pages/PasswordResetConfirmPage";
import AdminSurveys from "@/pages/AdminSurveys";
import AdminAnnouncements from "@/pages/AdminAnnouncements";
import AdminErlaeuterung from "@/pages/AdminErlaeuterung";
import ErlaeuterungPage from "@/pages/ErlaeuterungPage";
import { CookieBanner } from "@/components/CookieBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { Building2, ArrowRight, CheckCircle, CheckCircle2, Link as LinkIcon, Shield, Search, FileText, UserPlus, ShieldCheck, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function useMetaTags() {
  useEffect(() => {
    settingsApi.getMetaTags()
      .then(res => {
        const m = res.data;
        if (!m) return;
        if (m.site_title) document.title = m.site_title;
        const setMeta = (name, content, attr = 'name') => {
          if (!content) return;
          let el = document.querySelector(`meta[${attr}="${name}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
          el.setAttribute('content', content);
        };
        setMeta('description', m.meta_description);
        setMeta('robots', m.robots);
        setMeta('og:title', m.og_title, 'property');
        setMeta('og:description', m.og_description, 'property');
        setMeta('og:image', m.og_image, 'property');
        setMeta('og:type', m.og_type, 'property');
        setMeta('twitter:card', m.twitter_card);
        setMeta('twitter:title', m.og_title);
        setMeta('twitter:description', m.og_description);
        if (m.canonical_url) {
          let link = document.querySelector('link[rel="canonical"]');
          if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
          link.setAttribute('href', m.canonical_url);
        }
      })
      .catch(() => {});
  }, []);
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Laden...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Laden...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/admin" replace />;
  return children;
}

function HomePage() {
  const [homepageText, setHomepageText] = useState(null);
  const [seoSection, setSeoSection] = useState(null);
  const [showBannerCode, setShowBannerCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [analysisId, setAnalysisId] = useState('');
  const [analysesCount, setAnalysesCount] = useState(null);
  const navigate = useNavigate();
  useMetaTags();

  useEffect(() => {
    settingsApi.getHomepageText()
      .then(res => setHomepageText(res.data))
      .catch(() => console.log('Using default homepage text'));
    settingsApi.getSeoSection()
      .then(res => setSeoSection(res.data))
      .catch(() => console.log('Using default SEO section'));
    const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
    fetch(`${API}/api/public/stats`).then(r => r.json()).then(d => setAnalysesCount(d.analyses_count)).catch(() => {});
  }, []);

  const handleOpenAnalysis = (e) => {
    e.preventDefault();
    if (analysisId.trim()) {
      navigate(`/analyse?id=${analysisId.trim()}`);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <AnnouncementBanner />
      </div>
      <section className="py-12 md:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
            {/* Left: Hero Content */}
            <div className="flex-1">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight" data-testid="hero-title">
                Verwalterhonorar<br />
                <span className="text-[hsl(var(--brand-blue))]">objektbezogen berechnen</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-description">
                {homepageText?.hero_description || 'Verwaltungsbedarf analysieren. Angemessenes WEG-Verwalterhonorar ermitteln. VerwalterHonorar.de bewertet Gebäudezustand, technische Ausstattung, Verwaltungsstruktur und besondere Aufwandsfaktoren Ihrer Wohnungseigentümergemeinschaft – für eine nachvollziehbare Einschätzung und objektbezogene Honorarempfehlung.'}
              </p>
          
              {/* Benefits Section */}
              <div className="mt-8 flex flex-wrap gap-4" data-testid="benefits-section">
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 px-4 py-2 rounded-full">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span>Kostenfrei</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 px-4 py-2 rounded-full">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span>Unverbindlich</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 px-4 py-2 rounded-full">
                  <Shield className="h-4 w-4 text-emerald-600" />
                  <span>Keine Registrierung erforderlich</span>
                </div>
              </div>
          
              <div className="mt-10">
                <Link to="/analyse" className="block">
                  <Button size="lg" className="h-auto py-4 text-lg gap-2 bg-[hsl(var(--brand-blue))] hover:bg-[hsl(var(--brand-blue))]/90 w-full flex-col" data-testid="start-analysis-btn">
                    <span className="flex items-center gap-2">Jetzt eigene Analyse starten <ArrowRight className="h-5 w-5" /></span>
                    {analysesCount > 0 && (
                      <span className="flex items-center gap-2 text-xs font-normal opacity-80" data-testid="analyses-counter">
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-90"></span>
                          <span className="absolute inline-flex h-full w-full rounded-full bg-white/40"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 shadow-[0_0_8px_2px_rgba(255,255,255,0.5)]"></span>
                        </span>
                        {analysesCount.toLocaleString('de-DE')} Analysen erstellt
                      </span>
                    )}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Registration Card */}
            <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 order-last" data-testid="register-hint-home">
              <div className="rounded-xl border bg-gradient-to-b from-background to-secondary/20 p-6 space-y-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Kostenfreies Konto</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Registrieren Sie sich kostenfrei, um Ihre Analysen zu speichern und PDFs mit Ihrem eigenen Firmennamen zu erstellen.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    <span>Analysen dauerhaft speichern</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    <span>Eigener Firmenname im PDF</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    <span>DSGVO-konformer Datenexport</span>
                  </div>
                </div>
                <Link to="/registrieren" className="block">
                  <Button className="w-full gap-2" data-testid="register-hint-btn">
                    <UserPlus className="h-4 w-4" /> Jetzt registrieren
                  </Button>
                </Link>
                <p className="text-xs text-center text-muted-foreground">Dauerhaft kostenfrei — keine Daten nötig</p>
              </div>
              <div className="hidden lg:flex justify-center mt-6">
                <img src="/zertifizierter_verwalter.png" alt="Zertifizierter WEG Verwalter" className="w-28" data-testid="hero-seal-logo" />
              </div>
            </div>
          </div>

          {/* Mobile seal */}
          <div className="flex lg:hidden justify-center mt-8">
            <img src="/zertifizierter_verwalter.png" alt="Zertifizierter WEG Verwalter" className="w-24" data-testid="hero-seal-logo-mobile" />
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Subtle link info */}
          <p className="mt-10 text-sm text-muted-foreground flex items-center gap-2" data-testid="link-info-text">
            <LinkIcon className="h-4 w-4" />
            {homepageText?.footer_hint || 'Am Ende wird Ihnen ein permanenter Link angezeigt – speichern Sie ihn, um Ihre Analyse später aufzurufen.'}
          </p>
          
          {/* Open existing analysis section */}
          <div className="mt-6 pt-6 border-t border-dashed" data-testid="open-analysis-section">
            <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
              <Search className="h-4 w-4" />
              Bereits eine Analyse erstellt? Geben Sie Ihre Analyse-ID ein:
            </p>
            <form onSubmit={handleOpenAnalysis} className="flex gap-3 max-w-md">
              <Input
                type="text"
                placeholder="z.B. A-2026-XXXX"
                value={analysisId}
                onChange={(e) => setAnalysisId(e.target.value)}
                className="flex-1"
                data-testid="analysis-id-input"
              />
              <Button 
                type="submit" 
                variant="outline"
                disabled={!analysisId.trim()}
                data-testid="open-analysis-btn"
              >
                Öffnen
              </Button>
            </form>
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 border-t">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Gebäudeanalyse", desc: "Erfassung von Gebäudedaten, Besonderheiten und Ausstattungsmerkmalen" },
            { title: "Zustandsbewertung", desc: "Bewertung des Gebäudezustands und durchgeführter Modernisierungen" },
            { title: "Bedarfsermittlung", desc: "Berechnung des Verwaltungsbedarfs und der geschätzten Vergütung" },
          ].map((item, i) => (
            <div key={i} className="p-6 rounded-lg border hover:shadow-md transition-shadow duration-200" data-testid={`feature-card-${i}`}>
              <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center mb-4">
                <span className="text-sm font-bold text-foreground">{i + 1}</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile seal - shown below cards on small screens */}
      <div className="md:hidden flex justify-center py-8 border-t">
        <img src="/zertifizierter_verwalter.png" alt="Zertifizierter WEG Verwalter" className="h-24" data-testid="footer-seal-logo" />
      </div>

      {/* SEO Content Section - Fachliche Erläuterung (~800 Wörter) */}
      <section className="py-16 px-6 border-t" data-testid="seo-fachtext">
        <div className="max-w-4xl mx-auto space-y-10">

          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-3">Was berechnet VerwalterHonorar.de?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              VerwalterHonorar.de gibt nicht lediglich einen pauschalen Preis pro Einheit aus. Stattdessen bewertet es den konkreten Verwaltungsbedarf Ihrer Liegenschaft anhand von über 20 objektbezogenen Kriterien. Das Ergebnis ist eine nachvollziehbare Bewertung der Gebäudekomplexität, des Gebäudezustands und der Verwaltungshistorie – zusammengefasst in einem Gesamtscore, einer Bedarfskategorie und einer marktgerechten Honorarempfehlung. Die Berechnung berücksichtigt dabei nicht nur Standardfaktoren wie die Anzahl der Einheiten, sondern auch individuelle Aufwandstreiber wie Sanierungsrückstau, laufende Rechtsstreitigkeiten oder eine schwierige Eigentümerstruktur.
            </p>
          </div>

          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-3">Welche Faktoren werden berücksichtigt?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Die Analyse basiert auf drei Bewertungssäulen, die den Verwaltungsaufwand einer Wohnungseigentümergemeinschaft bestimmen:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 rounded-lg border bg-slate-50/50">
                <p className="text-sm font-semibold mb-2">Gebäudekomplexität</p>
                <ul className="text-xs text-muted-foreground space-y-1 leading-relaxed">
                  <li>Anzahl der Wohn- und Gewerbeeinheiten</li>
                  <li>Anzahl der Gebäude in der WEG</li>
                  <li>Aufzuganlage und Tiefgarage</li>
                  <li>Art der Heizungsanlage</li>
                  <li>Besondere technische Einrichtungen</li>
                </ul>
              </div>
              <div className="p-4 rounded-lg border bg-slate-50/50">
                <p className="text-sm font-semibold mb-2">Gebäudezustand</p>
                <ul className="text-xs text-muted-foreground space-y-1 leading-relaxed">
                  <li>Baualter und effektives Gebäudealter</li>
                  <li>Allgemeiner Gebäudezustand</li>
                  <li>Durchgeführte Modernisierungen</li>
                  <li>Anstehender Sanierungsbedarf</li>
                  <li>Aktuelle Reparaturbedarfe</li>
                </ul>
              </div>
              <div className="p-4 rounded-lg border bg-slate-50/50">
                <p className="text-sm font-semibold mb-2">Verwaltungshistorie</p>
                <ul className="text-xs text-muted-foreground space-y-1 leading-relaxed">
                  <li>Eigentümerversammlungen</li>
                  <li>Zahlungsverzug und Mahnverfahren</li>
                  <li>Rücklagenniveau</li>
                  <li>Rechtsstreitigkeiten</li>
                  <li>Verwalterwechsel und Erstbestellung</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-3">Für wen ist das Tool gedacht?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: 'Hausverwaltungen', text: 'Kalkulieren Sie Ihr Angebot auf Basis einer objektiven Bedarfsanalyse. VerwalterHonorar.de liefert eine marktgerechte Orientierung für Ihre Honorargestaltung.' },
                { title: 'Verwaltungsbeiräte', text: 'Bewerten Sie eingehende Verwalterangebote im Kontext Ihrer Liegenschaft. Verstehen Sie, ob ein Angebot zum Aufwand Ihres Objekts passt.' },
                { title: 'Wohnungseigentümergemeinschaften', text: 'Verstehen Sie, welcher Verwaltungsaufwand bei Ihrem Objekt zu erwarten ist und welche Vergütung dafür angemessen wäre.' },
                { title: 'Bauträger und Projektentwickler', text: 'Schätzen Sie die zu erwartenden Verwaltungskosten für Neubauprojekte bereits in der Planungsphase realistisch ein.' },
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-lg border">
                  <p className="text-sm font-semibold mb-1">{item.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-3">Was erhalten Sie als Ergebnis?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Nach der Analyse erhalten Sie eine prozentuale Bewertung des Verwaltungsbedarfs, eine Einschätzung der Objektkomplexität, eine objektbezogene Honorarempfehlung in EUR pro Einheit und Monat (netto), eine nachvollziehbare Begründung aller Bewertungsfaktoren sowie einen professionellen PDF-Report zum Download und Versand. Registrierte Nutzer können zusätzlich ihre Analysen dauerhaft speichern und PDFs mit eigenem Firmennamen erstellen.
            </p>
          </div>

          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-3">Wer steht hinter VerwalterHonorar.de?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Das WEG-Analysetool wird von der Scholze-Kurz &amp; Kurz Immobilien GmbH entwickelt und betrieben – einem erfahrenen WEG-Verwalter mit Tätigkeit seit 1981. Als zertifizierter Verwalter nach § 26a WEG kennen wir die Herausforderungen der Branche aus erster Hand. Das Tool wird stetig weiterentwickelt und bleibt dauerhaft kostenfrei nutzbar. Die Berechnungslogik basiert auf langjähriger praktischer Erfahrung in der WEG-Verwaltung und wird regelmäßig aktualisiert.
            </p>
          </div>

          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-3">Was kann das Ergebnis nicht ersetzen?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Die Berechnung ist eine objektbezogene Orientierung und Kalkulationsunterstützung. Sie ersetzt kein verbindliches Angebot einer konkreten Hausverwaltung. Regionale Marktgegebenheiten, individuelle Leistungsvereinbarungen und persönliche Beratung bleiben für die finale Honorargestaltung maßgeblich. VerwalterHonorar.de dient als transparente, datenbasierte Grundlage für fundierte Entscheidungen.
            </p>
          </div>

        </div>
      </section>

      {/* SEO Section from Admin */}
      {(seoSection?.heading || seoSection?.content) && (
        <section className="py-16 px-6 border-t bg-slate-50/50" data-testid="seo-section">
          <div className="max-w-6xl mx-auto">
            {seoSection.heading && (
              <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4" data-testid="seo-heading">{seoSection.heading}</h2>
            )}
            {seoSection.content && (
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="seo-content">{seoSection.content}</p>
            )}
          </div>
        </section>
      )}

      {/* Backlink Banner */}
      <section className="py-12 px-6 border-t" data-testid="backlink-section">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-sm font-semibold text-foreground mb-4">Verlinken Sie unser Tool auf Ihrer Webseite</h3>
          
          <div className="border rounded-lg p-4 bg-white mb-4" data-testid="backlink-preview">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-8 w-8 rounded-md bg-[#1BA1E4] flex items-center justify-center flex-shrink-0">
                <FileText className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm">VerwalterHonorar.de – das WEG-Analysetool</span>
            </div>
            {seoSection?.backlink_bullets && (
              <ul className="text-xs text-muted-foreground space-y-0.5 ml-11">
                {seoSection.backlink_bullets.split('|').filter(Boolean).slice(0, 3).map((b, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-[#1BA1E4] flex-shrink-0" />
                    {b.trim()}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            onClick={() => {
              setShowBannerCode(!showBannerCode);
              setCopied(false);
            }}
            className="text-xs text-[#1BA1E4] hover:underline font-medium"
            data-testid="toggle-banner-code"
          >
            {showBannerCode ? 'HTML-Code ausblenden' : 'HTML-Code anzeigen'}
          </button>
          
          {showBannerCode && (
            <div className="mt-3 relative">
              <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-lg overflow-x-auto" data-testid="banner-html-code">{`<a href="${window.location.origin}" target="_blank" rel="noopener" style="display:inline-block;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;text-decoration:none;color:#1e293b;font-family:sans-serif;background:#fff;max-width:280px">
  <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
    <div style="width:28px;height:28px;border-radius:6px;background:#1BA1E4;display:flex;align-items:center;justify-content:center">
      <svg width="14" height="14" fill="none" stroke="#fff" stroke-width="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
    </div>
    <strong style="font-size:13px">VerwalterHonorar.de – das WEG-Analysetool</strong>
  </div>${seoSection?.backlink_bullets ? `\n  <ul style="list-style:none;padding:0;margin:0 0 0 36px;font-size:11px;color:#64748b">${seoSection.backlink_bullets.split('|').filter(Boolean).slice(0, 3).map(b => `\n    <li style="margin-bottom:2px">&#10003; ${b.trim()}</li>`).join('')}\n  </ul>` : ''}
</a>`}</pre>
              <button
                onClick={() => {
                  const code = document.querySelector('[data-testid="banner-html-code"]').textContent;
                  navigator.clipboard.writeText(code);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="absolute top-2 right-2 text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-md transition-colors"
                data-testid="copy-banner-code"
              >
                {copied ? 'Kopiert!' : 'Kopieren'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Footer Links */}
      <footer className="py-8 px-6 border-t" data-testid="homepage-footer">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
          <Link to="/info" className="flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="footer-info-link">
            <ShieldCheck className="h-3.5 w-3.5" /> Sicherheit & Datenschutz
          </Link>
          <span className="text-border">|</span>
          <Link to="/faq" className="flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="footer-faq-link">
            <HelpCircle className="h-3.5 w-3.5" /> FAQ
          </Link>
          <span className="text-border">|</span>
          <Link to="/erlaeuterung" className="hover:text-foreground transition-colors" data-testid="footer-erlaeuterung-link">
            Erläuterung
          </Link>
          <span className="text-border">|</span>
          <Link to="/datenschutz" className="hover:text-foreground transition-colors" data-testid="footer-datenschutz-link">
            Datenschutzerklärung
          </Link>
          <span className="text-border">|</span>
          <Link to="/registrieren" className="hover:text-foreground transition-colors" data-testid="footer-register-link">
            Registrieren
          </Link>
          <span className="text-border">|</span>
          <Link to="/login" className="hover:text-foreground transition-colors" data-testid="footer-login-link">
            Verwalter-Login
          </Link>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <CookieBanner />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/analyse" element={<PublicWizard />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/info" element={<InfoPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/erlaeuterung" element={<ErlaeuterungPage />} />
            <Route path="/passwort-vergessen" element={<PasswordResetPage />} />
            <Route path="/passwort-reset" element={<PasswordResetConfirmPage />} />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/registrieren" element={<RegisterPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="analyse/neu" element={<AdminAnalysis />} />
            <Route path="analyse/:id" element={<AdminAnalysis />} />
            <Route path="pdf-firma" element={<PdfCompanyPage />} />
            <Route path="einstellungen" element={<AdminOnlyRoute><AdminSettings /></AdminOnlyRoute>} />
            <Route path="pdf-einstellungen" element={<AdminOnlyRoute><AdminPdfSettings /></AdminOnlyRoute>} />
            <Route path="nutzer" element={<AdminOnlyRoute><AdminUsers /></AdminOnlyRoute>} />
            <Route path="umfragen" element={<AdminOnlyRoute><AdminSurveys /></AdminOnlyRoute>} />
            <Route path="hinweise" element={<AdminOnlyRoute><AdminAnnouncements /></AdminOnlyRoute>} />
            <Route path="erlaeuterung" element={<AdminOnlyRoute><AdminErlaeuterung /></AdminOnlyRoute>} />
            <Route path="profil" element={<ProfilePage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
